import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createEntityResponsible, deleteEntityResponsible, getEntityResponsibles, setEntityPrimaryResponsible, updateEntityResponsible } from "../../api/entities/Responsible";
import { ResponsibleForm } from "../../types/forms/entity";

export const useEntityResponsible = (entityIdStr?: string) => {
  const queryClient = useQueryClient();
  const entityId = Number(entityIdStr);

  const queryKey = ["entity", entityId, "Responsible"];

  const result = useQuery({
    queryKey,
    queryFn: () => getEntityResponsibles(entityId),
    enabled: !!entityId,
  });

  const createMutation = useMutation({
    mutationFn: (values: ResponsibleForm) => createEntityResponsible(entityId, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ ResponsibleId, values }: { ResponsibleId: number, values: ResponsibleForm }) => updateEntityResponsible(entityId, ResponsibleId, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (ResponsibleId: number) => deleteEntityResponsible(entityId, ResponsibleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });

  const setPrimaryMutation = useMutation({
    mutationFn: (ResponsibleId: number) => setEntityPrimaryResponsible(entityId, ResponsibleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });

  return {
    responsiblesData: result.data ?? [],
    isPending: result.isPending,
    isError: result.isError,
    error: result.error,
    createResponsible: createMutation.mutateAsync,
    updateResponsible: updateMutation.mutateAsync,
    deleteResponsible: deleteMutation.mutateAsync,
    setPrimaryResponsible: setPrimaryMutation.mutateAsync,
    isUpdating: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending || setPrimaryMutation.isPending,
  }
}